import type { SupportTicketQueryScope } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import {
  activeViewStatusCountsBaseQueryKey,
  supportTicketCountsQueryKey,
  supportTicketQueryScopeKey,
  supportTicketsPageScopedQueryKey,
} from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import type { QueryClient } from "@tanstack/react-query";
import { supportTicketSummaryBaseQueryKey } from "./db/support-ticket-data-source";

const SUPPORT_TICKET_LIST_INVALIDATION_DELAY_MS = 2000;

const pendingInvalidationTimers = new WeakMap<
  QueryClient,
  Map<string, ReturnType<typeof setTimeout>>
>();

function queryScopeTimerKey(queryScope?: SupportTicketQueryScope): string {
  return JSON.stringify(supportTicketQueryScopeKey(queryScope));
}

export function invalidateSupportTicketListQueriesAfterDelay({
  queryClient,
  queryScope,
}: {
  queryClient: QueryClient;
  queryScope?: SupportTicketQueryScope;
}) {
  const scopeKey = queryScopeTimerKey(queryScope);
  let pendingTimers = pendingInvalidationTimers.get(queryClient);
  if (!pendingTimers) {
    pendingTimers = new Map();
    pendingInvalidationTimers.set(queryClient, pendingTimers);
  }

  if (pendingTimers.has(scopeKey)) {
    return;
  }

  const timeout = setTimeout(() => {
    pendingTimers.delete(scopeKey);
    void queryClient.invalidateQueries({
      queryKey: supportTicketsPageScopedQueryKey(queryScope),
    });
    void queryClient.invalidateQueries({
      queryKey: supportTicketCountsQueryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: activeViewStatusCountsBaseQueryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: supportTicketSummaryBaseQueryKey,
    });
  }, SUPPORT_TICKET_LIST_INVALIDATION_DELAY_MS);
  pendingTimers.set(scopeKey, timeout);
}
